import { formatEmailHtmlWithSignature } from "./emailHtmlFormat.ts";

export const escapeHtml = (value: string) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const ATTR_VALUE_RE = /([a-z0-9:-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/gi;

function decodeHtmlEntities(value: string): string {
  return value.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos|nbsp);/gi, (full, code: string) => {
    const c = code.toLowerCase();
    if (c === "amp") return "&";
    if (c === "lt") return "<";
    if (c === "gt") return ">";
    if (c === "quot") return '"';
    if (c === "apos") return "'";
    if (c === "nbsp") return " ";
    const n = c.startsWith("#x") ? Number.parseInt(c.slice(2), 16) : Number.parseInt(c.slice(1), 10);
    try {
      return String.fromCodePoint(n);
    } catch {
      return full;
    }
  });
}

/** Attribute value with HTML entities decoded (callers re-escape on output). */
function readHtmlAttr(attrsRaw: string, name: string): string {
  const wanted = name.toLowerCase();
  ATTR_VALUE_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = ATTR_VALUE_RE.exec(String(attrsRaw || "")))) {
    if (String(match[1] || "").toLowerCase() !== wanted) continue;
    return decodeHtmlEntities(String(match[2] ?? match[3] ?? match[4] ?? "")).trim();
  }
  return "";
}

/**
 * Mail clients often paste emoji as <img> (Outlook/Gmail). Keep the character via
 * alt/title, or recover a codepoint from common emoji CDN URLs when alt is empty.
 */
function isEmojiCdnSrc(src: string): boolean {
  return (
    /mail\.google\.com\/mail\/e\//i.test(src) ||
    /notoemoji/i.test(src) ||
    /twemoji/i.test(src) ||
    /emoji-datasource/i.test(src) ||
    /s\.w\.org\/images\/core\/emoji/i.test(src)
  );
}

export function emojiTextFromImgAttrs(attrsRaw: string): string {
  const fromLabel = readHtmlAttr(attrsRaw, "alt") || readHtmlAttr(attrsRaw, "title");
  if (fromLabel) return fromLabel;

  const src = readHtmlAttr(attrsRaw, "src");
  if (!src) return "";
  if (!isEmojiCdnSrc(src)) return "";

  const codeMatch =
    src.match(/mail\.google\.com\/mail\/e\/([0-9a-f]{2,8}(?:-[0-9a-f]{2,8})*)/i) ||
    src.match(/(?:^|[/_-])((?:[0-9a-f]{4,6})(?:-(?:[0-9a-f]{4,6}))*)(?:\.[a-z0-9]+)?(?:$|[/?#])/i);
  if (!codeMatch?.[1]) return "";
  try {
    return codeMatch[1]
      .split("-")
      .map((hex) => String.fromCodePoint(Number.parseInt(hex, 16)))
      .join("");
  } catch {
    return "";
  }
}

/** Emoji incl. ZWJ, variation selector, skin tones and flags — but not plain digits. */
const EMOJI_ONLY_RE = /^[\p{Extended_Pictographic}\u{1F3FB}-\u{1F3FF}\u{1F1E6}-\u{1F1FF}️‍\s]+$/u;

/** Hosts whose image URLs only work for the logged-in mailbox owner (e.g. Gmail attachment views). */
const LOGIN_GATED_IMAGE_HOST_RE =
  /^https:\/\/(?:mail\.google\.com|mail\.yahoo\.com|outlook\.(?:live|office|office365)\.com|attachments\.office\.net)\//i;

/** Our own upload route on a local dev server (APP_URL=http://localhost:…) — only for local testing. */
const LOCAL_DEV_SIGNATURE_IMAGE_RE =
  /^http:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?\/api\/public\/signature-images\/[^\s"'<>]+$/i;

/** Image a recipient's mail client can actually load: public https, not behind a mailbox login. */
export function isPublicSignatureImageSrc(src: string): boolean {
  const value = String(src || "").trim();
  if (LOCAL_DEV_SIGNATURE_IMAGE_RE.test(value)) return true;
  return /^https:\/\/[^\s"'<>]+$/i.test(value) && !LOGIN_GATED_IMAGE_HOST_RE.test(value);
}

/**
 * How a signature keeps a pasted image: as emoji glyph, as <img>, or not at all (recipients
 * could not load it — the editor then asks for an upload).
 */
export function signatureImageKind(src: string, alt: string): "emoji" | "public" | "unusable" {
  if ((alt && EMOJI_ONLY_RE.test(alt)) || (!alt && isEmojiCdnSrc(src))) return "emoji";
  return isPublicSignatureImageSrc(src) ? "public" : "unusable";
}

/** Inline CSS kept in signatures — typography and box layout; no backgrounds, positioning or url(). */
const SIGNATURE_STYLE_PROPS = new Set([
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "color",
  "text-decoration",
  "text-align",
  "vertical-align",
  "line-height",
  "letter-spacing",
  "white-space",
  "width",
  "height",
  "max-width",
  "margin",
  "margin-top",
  "margin-right",
  "margin-bottom",
  "margin-left",
  "padding",
  "padding-top",
  "padding-right",
  "padding-bottom",
  "padding-left",
  "border",
  "border-top",
  "border-right",
  "border-bottom",
  "border-left",
  "border-collapse",
]);

function sanitizeInlineStyle(styleRaw: string): string {
  const out: string[] = [];
  for (const decl of String(styleRaw || "").split(";")) {
    const idx = decl.indexOf(":");
    if (idx <= 0) continue;
    const prop = decl.slice(0, idx).trim().toLowerCase();
    const value = decl.slice(idx + 1).trim();
    if (!SIGNATURE_STYLE_PROPS.has(prop) || !value) continue;
    // Browsers copy computed defaults (font-style: normal, text-align: start …) — noise only.
    if (/^(normal|initial|inherit|unset|start)$/i.test(value)) continue;
    if (/url\s*\(|expression\s*\(|javascript:|[<>\\]|@import/i.test(value)) continue;
    out.push(`${prop}: ${value}`);
  }
  return out.join("; ");
}

const SIGNATURE_EXTRA_TAGS = new Set(["table", "thead", "tbody", "tr", "td", "th", "hr", "font", "img"]);

/** style/width/align… for the signature variant; values are validated and re-escaped. */
function signatureAttrs(tagName: string, attrsRaw: string): string {
  const parts: string[] = [];
  const style = sanitizeInlineStyle(readHtmlAttr(attrsRaw, "style"));
  if (tagName === "font") {
    // <font face color> (Gmail/Outlook) → equivalent inline CSS on a span.
    const extra = sanitizeInlineStyle(
      [
        readHtmlAttr(attrsRaw, "face") && `font-family: ${readHtmlAttr(attrsRaw, "face")}`,
        readHtmlAttr(attrsRaw, "color") && `color: ${readHtmlAttr(attrsRaw, "color")}`,
      ]
        .filter(Boolean)
        .join(";"),
    );
    const merged = [extra, style].filter(Boolean).join("; ");
    return merged ? ` style="${escapeHtml(merged)}"` : "";
  }
  if (style) parts.push(`style="${escapeHtml(style)}"`);
  for (const name of ["width", "height", "border", "cellpadding", "cellspacing"]) {
    const v = readHtmlAttr(attrsRaw, name);
    if (/^\d{1,4}(?:px|%)?$/i.test(v)) parts.push(`${name}="${v}"`);
  }
  for (const name of ["align", "valign"]) {
    const v = readHtmlAttr(attrsRaw, name).toLowerCase();
    if (/^(left|right|center|top|middle|bottom|baseline)$/.test(v)) parts.push(`${name}="${v}"`);
  }
  return parts.length ? ` ${parts.join(" ")}` : "";
}

type SanitizeRichEmailOptions = {
  /** Signatures: keep inline styles, tables, <font> and public https images. */
  keepFormatting?: boolean;
};

export const sanitizeRichEmailHtml = (raw: unknown, options: SanitizeRichEmailOptions = {}) => {
  const keepFormatting = options.keepFormatting === true;
  let html = String(raw ?? "").trim();
  if (!html) return "";
  html = html
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, "")
    .replace(/\son\w+\s*=\s*'[^']*'/gi, "")
    .replace(/\son\w+\s*=\s*[^\s>]+/gi, "");

  const allowedTags = new Set(["p", "br", "strong", "b", "em", "i", "u", "a", "ul", "ol", "li", "span", "div"]);
  html = html.replace(/<\/?([a-z0-9-]+)([^>]*)>/gi, (full, tagNameRaw, attrsRaw) => {
    const tagName = String(tagNameRaw || "").toLowerCase();
    const isClosing = full.startsWith("</");
    const attrs = String(attrsRaw || "");
    if (tagName === "img" && !isClosing) {
      const src = readHtmlAttr(attrs, "src");
      const alt = readHtmlAttr(attrs, "alt") || readHtmlAttr(attrs, "title");
      const kind = signatureImageKind(src, alt);
      // Pasted emoji icons are usually <img alt="💙"> — keep the glyph, drop the image.
      // Escape: alt/title is inserted after this single sanitize pass.
      if (!keepFormatting || kind === "emoji") return escapeHtml(emojiTextFromImgAttrs(attrs));
      // Images recipients cannot load (Gmail attachment views, cid:, data:, file:) are dropped —
      // the editor asks for an upload instead of leaving "page1image…" alt text behind.
      if (kind === "unusable") return "";
      const altAttr = alt ? ` alt="${escapeHtml(alt)}"` : ' alt=""';
      return `<img src="${escapeHtml(src)}"${altAttr}${signatureAttrs("img", attrs)}>`;
    }
    if (!allowedTags.has(tagName) && !(keepFormatting && SIGNATURE_EXTRA_TAGS.has(tagName))) {
      return "";
    }
    if (tagName === "font") return isClosing ? "</span>" : `<span${signatureAttrs("font", attrs)}>`;
    if (isClosing) return `</${tagName}>`;
    if (tagName === "br") return "<br/>";
    if (tagName === "hr") return `<hr${signatureAttrs("hr", attrs)}>`;
    if (tagName === "a") {
      const attrs = String(attrsRaw || "");
      const hrefRaw = readHtmlAttr(attrs, "href");
      const hrefLower = hrefRaw.toLowerCase();
      const safeHref =
        hrefRaw &&
        (hrefLower.startsWith("http://") ||
          hrefLower.startsWith("https://") ||
          hrefLower.startsWith("mailto:") ||
          hrefLower.startsWith("tel:"))
          ? hrefRaw
          : "";
      const styleAttr = keepFormatting ? signatureAttrs("a", attrs) : "";
      return safeHref
        ? `<a href="${escapeHtml(safeHref)}" rel="noopener noreferrer"${styleAttr}>`
        : `<a${styleAttr}>`;
    }
    return keepFormatting ? `<${tagName}${signatureAttrs(tagName, attrs)}>` : `<${tagName}>`;
  });
  return html.trim();
};

/**
 * Signature storage/editor: keeps formatting (fonts, colours, tables, public images) on top of
 * the shared sanitize, plus Outlook/Gmail orphan-emoji layout fixes.
 */
export const sanitizeSignatureHtml = (raw: unknown) => {
  const html = sanitizeRichEmailHtml(raw, { keepFormatting: true });
  if (!html) return "";
  return normalizeSignatureEmojiLayout(html).trim();
};

/** Matches emoji sequences (incl. ZWJ / variation selectors). */
const SIGNATURE_EMOJI_RE = String.raw`(?:[\p{Extended_Pictographic}\uFE0F\u200D]+)`;

/**
 * Outlook/Gmail often put pasted emoji on their own block/line. Pull them back
 * next to the surrounding text so the editor matches the intended signature layout.
 */
export function normalizeSignatureEmojiLayout(html: string): string {
  let out = String(html ?? "");
  if (!out) return "";

  // <div>💙</div> / <p>👉</p> → inline glyph
  out = out.replace(
    new RegExp(
      String.raw`<(div|p)(?:\s[^>]*)*>(?:&nbsp;|\s|<br\s*\/?>)*(${SIGNATURE_EMOJI_RE})(?:&nbsp;|\s|<br\s*\/?>)*<\/\1>`,
      "gu",
    ),
    " $2 ",
  );

  // <div>Gemeinsam Gutes tun</div> 💙 → <div>Gemeinsam Gutes tun 💙</div>
  out = out.replace(
    new RegExp(
      String.raw`<(div|p)((?:\s[^>]*)?)>((?:[^<]|<(?!\/\1>))*)<\/\1>\s*(${SIGNATURE_EMOJI_RE})`,
      "gu",
    ),
    "<$1$2>$3 $4</$1>",
  );

  // Orphan emoji before a link → keep with the link (preserve leading breaks)
  out = out.replace(
    new RegExp(
      String.raw`((?:<br\s*\/?>\s*)*)(${SIGNATURE_EMOJI_RE})(?:&nbsp;|\s|<br\s*\/?>)*(<a\b)`,
      "gu",
    ),
    "$1$2 $3",
  );

  // Remaining emoji stuck between breaks → join previous line, keep a paragraph break
  out = out.replace(
    new RegExp(String.raw`(?:<br\s*\/?>\s*)+(${SIGNATURE_EMOJI_RE})(?:\s*<br\s*\/?>)+`, "gu"),
    " $1<br/><br/>",
  );

  return out.replace(/ {2,}/g, " ");
}

/**
 * Reflows contentEditable output into email-safe paragraphs.
 *
 * The reply/compose editors use `white-space: pre-wrap`, so line breaks arrive
 * either as raw `\n` characters or as `<div>` blocks. Both look correct while
 * editing, but raw newlines collapse to spaces in real HTML/email clients.
 * Well-formed content (AI drafts, lists) is left untouched.
 */
function normalizeEditorHtmlBlocks(rawHtml: string) {
  const html = String(rawHtml ?? "").trim();
  if (!html) return "";
  // Already structured (AI/markdown drafts, lists) — keep intact.
  if (/<(p|ul|ol|li)\b/i.test(html)) return html;
  // Plain single line without any breaks — nothing to reflow.
  if (!/<div\b/i.test(html) && !/<br\b/i.test(html) && !/\n/.test(html)) return html;

  const work = html
    .replace(/<div\b[^>]*>/gi, "\n")
    .replace(/<\/div>/gi, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  if (!work) return "";

  const paragraphs = work
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (paragraphs.length === 0) return "";

  return paragraphs.map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");
}

export const sanitizeEditorHtml = (raw: unknown) => {
  // Compose/reply bodies: shared sanitize only — do not apply signature emoji layout merges.
  const sanitized = sanitizeRichEmailHtml(raw);
  if (!sanitized) return "";
  return normalizeEditorHtmlBlocks(sanitized);
};

export const plainTextToEmailHtml = (textRaw: string) => {
  const normalized = String(textRaw || "").replace(/\r\n/g, "\n").trim();
  const paragraphs = normalized
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  const htmlParas = (paragraphs.length ? paragraphs : [normalized])
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br/>")}</p>`)
    .join("");
  return `<div>${htmlParas}</div>`;
};

export const buildEmailBodyWithSignature = (
  contentRaw: string,
  signatureHtmlRaw: unknown,
  options?: { isHtml?: boolean },
) => {
  const bodyHtml = options?.isHtml ? sanitizeEditorHtml(contentRaw) : plainTextToEmailHtml(contentRaw);
  const signatureHtml = sanitizeSignatureHtml(signatureHtmlRaw);
  return formatEmailHtmlWithSignature(bodyHtml, signatureHtml || null);
};

export function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}
